import type { PodBrand } from '@/types/pod'

/**
 * The South West Steel Buildings pod — branding layer (browser side).
 *
 * A SALES DEMO, NOT A CUSTOMER. South West Steel Buildings Ltd (Chard,
 * Somerset — company no. 17078070) has not bought anything and has not signed
 * a DPA. This pod exists to be shown to their director. Consequences, all
 * deliberate:
 *   - registry entry carries `billing: 'operator'` and NO `customerSince`
 *   - `notifyPhone` is the OPERATOR'S mobile — a captured enquiry here is a
 *     demo artefact, not their lead, and must not reach them
 *   - no `publishDomain`, no `missedCall` block: both point at a domain and a
 *     phone line the operator does not control
 * If they buy, all four of those flip and the ONBOARDING.md paperwork gate is
 * the first step, not the last.
 *
 * SOURCE OF TRUTH. Every value below is lifted from their live Wix site
 * (southweststeelbuildings.co.uk, read 11 Sep 2026): the office number and
 * sales address from the site footer, the tagline from the hero, the four
 * sectors from the homepage cards. Nothing is invented — the discipline the
 * grounding fence enforces on the corpus applies to the config too.
 *
 * NOT TO BE CONFUSED WITH South West Steel Construction Ltd (Holsworthy,
 * Devon — RIDBA member, own factory, hot-rolled). Different company, similar
 * name, ranks for the same terms. None of their material belongs in this pod.
 *
 * PALETTE. Their logo and CTA buttons: dark navy wordmark with an orange
 * accent, on white. Taken from the live site chrome so the assistant reads as
 * their own front desk. Tune once seen live in the engine UI.
 *
 * CONTACT. Office landline and the sales inbox, both published on every page.
 * The landline diverts to the director's mobile and is slow to answer — which
 * is the reason for the demo, and also why the assistant should lean on
 * capture rather than on telling people to ring.
 *
 * builtByForedesk IS ON, uniquely among live-facing pods. This is a pitch
 * asset shown to a prospect, not a business's own front of house, so the
 * credit is wanted. If they buy, revisit it as the commercial decision the
 * field's contract says it is.
 */
export const swsteelPod: PodBrand = {
  id: 'swsteel',
  brandName: 'South West Steel Buildings',
  productName: 'South West Steel Buildings',
  assistantName: 'Stan',
  tagline: 'Steel buildings supplied across the South West',
  roleLabel: 'steel buildings assistant',
  // "DOMESTIC OUTBUILDINGS", NOT "RESIDENTIAL" (11 Sep 2026). The earlier
  // wording offered "residential", which to a reader means a house. For this
  // business it means a workshop, a garage or a garden store — never a
  // dwelling, an annexe or a barn conversion, which are a different
  // proposition on Building Regs and warranty and are not committed to from a
  // chat window. A greeting that invites the wrong enquiry wastes the
  // enquirer's evening and the office's morning.
  welcomeIntro:
    "Planning a steel building? Tell me roughly what you're after — width, length and height, what it's for, and the postcode of the site — and I'll get a budget price moving. Farm buildings, industrial and commercial units, and domestic outbuildings like workshops and garages, anywhere in the South West. Got drawings already? Even better.",
  starters: [
    'Do you supply only, or install as well?',
    'Do I need planning permission?',
    'How long from order to delivery?',
    'What do you need to give me a price?',
  ],
  inputPrompt: 'Ask Stan about your building',
  footerNote:
    "Stan answers from South West Steel Buildings' own information — anything else goes straight to the office.",
  builtByForedesk: true,
  theme: {
    // Their site chrome: navy wordmark, orange accent, white surfaces.
    '--color-brand-primary': '31 48 66', // navy #1F3042
    '--color-brand-primary-hover': '20 32 45', // #14202D
    '--color-brand-accent': '232 99 26', // orange #E8631A
    '--color-surface-base': '248 250 251',
    '--color-surface-raised': '255 255 255',
    '--color-surface-subtle': '240 243 246',
    '--color-text-primary': '31 48 66',
    '--color-text-muted': '100 116 132',
    '--color-text-danger': '178 47 47',
    '--color-border-subtle': '223 229 235',
  },
  contact: {
    phone: '01460 203575',
    email: 'sales@southweststeelbuildings.co.uk',
  },
}
